/**
 * Data Transformers for Chat Operations
 * 
 * Single source of truth for data transformations
 * Architecture: Transformations happen at query level via `select` option
 * 
 * @module chatQueries/transformers
 */

import { sortChatsByLastMessageTime } from '../../../../components/chat/utils/messageUtils';
import { resolveMessageAttachmentMeta } from '../../../../components/chat/utils/fileUtils';

const toReplyRole = (senderType) => {
  const normalized = String(senderType ?? '').toLowerCase();
  if (normalized === 'customer') return 'user';
  if (normalized === 'agent' || normalized === 'bot' || normalized === 'assistant') return 'assistant';
  return 'assistant';
};

/**
 * Transform conversation data to chat format
 * Returns only essential fields for tab display (reduces memory usage)
 */
export const transformConversationToChat = (chat) => {
  const contact = chat.primary_contact;
  
  // Determine display name: use name if it exists and is not empty, otherwise use phone number
  const displayName = (contact?.name && contact.name.trim() !== '') 
    ? contact.name.trim() 
    : contact?.wa_phone || `Contact ${contact?.id || chat.id}`;
  
  // Handle owner field - can be a number (ID) or an object with id and name
  let owner = null;
  if (chat.owner) {
    if (typeof chat.owner === 'object' && chat.owner.id) {
      // Owner is an object with id and name
      owner = {
        id: chat.owner.id,
        name: chat.owner.name
      };
    } else if (typeof chat.owner === 'number') {
      // Owner is just a number (ID)
      owner = {
        id: chat.owner,
        name: null
      };
    }
  }

  return {
    id: chat.id,
    user_id: owner?.id || contact?.id || chat.id,
    name: displayName,
    subject: chat.subject || `WhatsApp Chat ${chat.id}`,
    channel: chat.channel,
    status: chat.status,
    owner: owner,
    bot_active: chat.bot_active,
    last_customer_msg_at: chat.last_customer_msg_at,
    last_agent_msg_at: chat.last_agent_msg_at,
    created_at: chat.created_at,
    updated_at: chat.updated_at,
    primary_contact: contact ? {
      id: contact.id,
      name: contact.name,
      wa_phone: contact.wa_phone
    } : null,
    last_time: chat.last_customer_msg_at || chat.created_at,
    last_message: chat.last_message || '',
    last_msg_body: chat.last_msg_body || '',
    last_msg_media_kind: chat.last_msg_media_kind || '',
    unread_count: 0
  };
};

/**
 * Transform and sort chats array
 */
export const transformAndSortChats = (chatsArray) => {
  if (!chatsArray || chatsArray.length === 0) {
    return [];
  }
  const transformedChats = chatsArray.map(transformConversationToChat);
  return sortChatsByLastMessageTime(transformedChats);
};

/**
 * Extract chats array from various response formats
 * Handles all API response formats including isSuccess wrapper
 */
export const extractChatsArray = (response) => {
  if (!response) {
    return [];
  }
  
  // Format 1: Direct array response
  if (Array.isArray(response)) {
    return response;
  }
  
  // Format 2: { isSuccess: true, data: [...] }
  if (response.isSuccess && response.data && Array.isArray(response.data)) {
    return response.data;
  }
  
  // Format 3: { data: [...] } (direct array in data)
  if (Array.isArray(response.data)) {
    return response.data;
  }
  
  // Format 4: { data: { data: [...] } } (nested)
  if (response.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  
  // Format 5: { isSuccess: true, data: { data: [...] } } (nested with isSuccess)
  if (response.isSuccess && response.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  
  return [];
};

/**
 * Extract a cursor-paginated chat-list page from the API envelope:
 * { isSuccess, data: { results: [...], pagination: { next_cursor, has_more, limit } } }
 *
 * Falls back to extractChatsArray (single page, no more) for any endpoint that
 * hasn't been migrated to the paginated envelope yet, so callers never crash.
 */
export const extractChatListPage = (response) => {
  const data = response?.data;
  const results = Array.isArray(data?.results) ? data.results : null;

  if (!results) {
    return { items: extractChatsArray(response), hasMore: false, nextCursor: null, limit: null };
  }

  const pagination = data?.pagination || response?.pagination || {};
  return {
    items: results,
    hasMore: !!pagination.has_more,
    nextCursor: pagination.next_cursor ?? null,
    limit: pagination.limit ?? null
  };
};

/**
 * Transform message data to consistent format
 */
export const transformMessage = (message, conversationId) => {
  // Build attachments array from media data if file exists
  let attachments = message.attachments || [];
  if (message.file && !attachments.length) {
    const fileName = message.file.split('/').pop() || 'attachment';
    const { type: attachmentType, mediaKind } = resolveMessageAttachmentMeta(
      fileName,
      message.media_kind || '',
      message.media_mime || ''
    );

    attachments.push({
      name: fileName,
      url: message.file,
      size: message.media_bytes || 0,
      type: attachmentType,
      mediaKind,
      caption: message.caption || '',
      // Duration can be string (MM:SS or HH:MM:SS) or number (seconds) from API
      duration: (mediaKind === 'audio' || mediaKind === 'voice') ? (message.duration || message.audio_duration || null) : null
    });
  }
  
  // Build audio object if it's an audio message
  let audio = message.audio || null;
  if (message.media_kind === 'audio' && message.file && !audio) {
    audio = {
      url: message.file,
      // Duration can be string (MM:SS or HH:MM:SS) or number (seconds) from API
      duration: message.duration || message.audio_duration || null
    };
  }

  const replyPreview = message.reply_to_preview || null;
  const replyTo =
    message.reply_to != null
      ? {
          id: replyPreview?.id ?? message.reply_to,
          content: replyPreview?.body || '',
          role: toReplyRole(replyPreview?.sender_type),
          timestamp: replyPreview?.created_at || null,
          senderName: replyPreview?.sender_name || null,
          senderId: replyPreview?.sender_id ?? null
        }
      : null;
  
  return {
    id: message.id,
    conversation_id: message.conversation_id || conversationId,
    sender_type: message.sender_type,
    sender_user: message.sender_user,
    sender_contact: message.sender_contact,
    sender_name: message.sender_name || null,
    sender_profile_pic: message.sender_profile_pic || null,
    content: message.body || message.content || '',
    caption: message.caption || '',
    media_kind: message.media_kind || '',
    media_mime: message.media_mime || '',
    media_bytes: message.media_bytes || null,
    file: message.file || null,
    wa_media_id: message.wa_media_id || '',
    meta_msg_id: message.meta_msg_id || '',
    meta_status: message.meta_status || '',
    timestamp: message.created_at,
    role: message.sender_type === 'Customer' ? 'user' : 'assistant',
    user_id: message.conversation_id || conversationId,
    attachments: attachments,
    audio: audio,
    reply_to: message.reply_to ?? null,
    reply_to_preview: replyPreview,
    replyTo,
    deleted: message.Is_Delete || false,
    edited: message.Is_Edit || false
  };
};

/**
 * Extract messages array from various response formats
 * Handles all API response formats including isSuccess wrapper
 */
export const extractMessagesArray = (response) => {
  if (!response) {
    return [];
  }
  
  // Format 1: Direct array response
  if (Array.isArray(response)) {
    return response;
  }
  
  // Format 2: { isSuccess: true, data: [...] }
  if (response.isSuccess && response.data && Array.isArray(response.data)) {
    return response.data;
  }
  
  // Format 3: { data: [...] } (direct array in data)
  if (Array.isArray(response.data)) {
    return response.data;
  }
  
  // Format 4: { messages: [...] }
  if (Array.isArray(response.messages)) {
    return response.messages;
  }
  
  // Format 5: { results: [...] }
  if (Array.isArray(response.results)) {
    return response.results;
  }
  
  // Format 6: { data: { data: [...] } } (nested)
  if (response.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  
  // Format 7: { isSuccess: true, data: { data: [...] } } (nested with isSuccess)
  if (response.isSuccess && response.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  
  return [];
};

/**
 * Extract pagination info from response
 * Handles multiple response formats:
 * 1. { pagination: { hasNext: true } } - from chatService.js wrapper
 * 2. { next: true, previous: false } - direct API response
 * 3. { data: { next: true } } - nested response
 * 4. { data: { pagination: { hasNext: true } } } - nested pagination
 */
export const extractPaginationInfo = (response) => {
  if (!response || typeof response !== 'object') {
    return { hasMore: false };
  }
  
  // Format 1: Direct pagination object (from chatService.js wrapper)
  if (response.pagination && typeof response.pagination === 'object') {
    // Check for hasNext (boolean) or hasMore
    if ('hasNext' in response.pagination) {
      return { hasMore: !!response.pagination.hasNext };
    }
    if ('hasMore' in response.pagination) {
      return { hasMore: !!response.pagination.hasMore };
  }
  }
  
  // Format 2: Direct next/previous fields (API response format)
  if ('next' in response) {
    return { hasMore: !!response.next };
  }
  
  // Format 3: Nested in data object
  if (response.data && typeof response.data === 'object') {
    // Check for direct next field in data
    if ('next' in response.data) {
      return { hasMore: !!response.data.next };
    }
    
    // Check for pagination object in data
    if (response.data.pagination && typeof response.data.pagination === 'object') {
      if ('hasNext' in response.data.pagination) {
        return { hasMore: !!response.data.pagination.hasNext };
      }
      if ('hasMore' in response.data.pagination) {
        return { hasMore: !!response.data.pagination.hasMore };
      }
    }
  }
  
  return { hasMore: false };
};

/**
 * Normalize pagination info to always use hasMore
 * Converts hasNext (boolean) to hasMore format
 * 
 * @param {object|null} paginationInfo - Pagination object that may have hasNext or hasMore
 * @param {object} response - Full response object for fallback extraction
 * @returns {object} Normalized pagination object with hasMore property
 */
export const normalizePaginationInfo = (paginationInfo, response = null) => {
  // If paginationInfo is provided and is an object
  if (paginationInfo && typeof paginationInfo === 'object') {
    // Convert hasNext to hasMore if needed
    if ('hasNext' in paginationInfo && !('hasMore' in paginationInfo)) {
      return { hasMore: !!paginationInfo.hasNext };
    }
    // If already has hasMore, return as is
    if ('hasMore' in paginationInfo) {
      return { hasMore: !!paginationInfo.hasMore };
    }
  }
  
  // Fallback: extract from response if provided
  if (response) {
    return extractPaginationInfo(response);
  }
  
  return { hasMore: false };
};

/**
 * Whether the API restricts this conversation to template messages only
 * (e.g. response.data.only_template_allow).
 */
export const extractOnlyTemplateAllowFlag = (result) => {
  if (result == null || typeof result !== 'object') return false;
  if (result.only_template_allow === true || result.onlyTemplateAllow === true) return true;
  const inner = result.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    if (inner.only_template_allow === true || inner.onlyTemplateAllow === true) return true;
  }
  return false;
};

/**
 * Extract messages, transform them, and extract pagination info
 * Shared utility for consistent message processing across the codebase
 * 
 * @param {object} result - Query result object (may have data property or be direct response)
 * @param {number|string} conversationId - Conversation ID for message transformation
 * @param {boolean} sortMessages - Whether to sort messages by timestamp (default: false)
 * @returns {object} Object with messages array, hasMore boolean, and onlyTemplateAllow boolean
 */
export const extractAndTransformMessagesWithPagination = (result, conversationId, sortMessages = false) => {
  const onlyTemplateAllow = extractOnlyTemplateAllowFlag(result);

  // Keep outer object when API returns { data: Message[], only_template_allow, next, ... }
  const useFullResultAsResponse =
    result &&
    typeof result === 'object' &&
    !Array.isArray(result) &&
    Array.isArray(result.data);
  const response = useFullResultAsResponse ? result : (result?.data || result);

  if (!response) {
    return { messages: [], hasMore: false, onlyTemplateAllow };
  }

  // Extract messages array
  const messagesData = extractMessagesArray(response);

  let paginationInfo = null;
  if (response && typeof response === 'object' && response.pagination) {
    paginationInfo = response.pagination;
  }
  const normalizedPagination = normalizePaginationInfo(paginationInfo, response);

  if (messagesData.length === 0) {
    return { messages: [], hasMore: normalizedPagination.hasMore, onlyTemplateAllow };
  }

  // Transform messages
  const transformedMessages = messagesData.map(msg => transformMessage(msg, conversationId));

  // Sort if requested
  const finalMessages = sortMessages
    ? transformedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    : transformedMessages;

  return {
    messages: finalMessages,
    hasMore: normalizedPagination.hasMore,
    onlyTemplateAllow
  };
};

